import { useEffect, useMemo, useRef } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import { DesignCanvas } from '../../../Design/DesignCanvas'
import { DESIGN_CANVAS_PRESETS, normalizeDesignCanvas } from '../../../Design/canvasPresets'
import { useDesignEditor } from '../../../Design/useDesignEditor'
import { Button } from '../../../Common/Button'
import { NumberStepper } from '../../../Common/NumberStepper'
import { type DesignCanvasSettings, type DesignConnectorShape, type DesignShape } from '../../../../types/design'
import {
  type DesignPayloadNode,
  type DesignPayloadConnector,
  type DesignPayload,
  normalizeDesignPayload,
  designPayloadToShapes,
} from './designPayload'

export type { DesignPayloadNode, DesignPayloadConnector, DesignPayload }

type DesignPayloadEditorProps = {
  value: DesignPayload
  onChange: (next: DesignPayload) => void
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? value as Record<string, unknown> : {}

const parseLanes = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)

const reorder = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

const shapesToPayload = (base: DesignPayload, shapes: DesignShape[]): DesignPayload => ({
  ...base,
  nodes: shapes
    .filter((shape) => shape.type !== 'CONNECTOR')
    .map((shape) => ({
      id: shape.id,
      type: shape.type === 'DIAMOND' ? 'decision' : 'stage',
      x: Math.round(shape.position.x),
      y: Math.round(shape.position.y),
      label: shape.text ?? shape.name ?? shape.id,
    })),
  connectors: shapes
    .filter((shape): shape is DesignConnectorShape => shape.type === 'CONNECTOR')
    .map((shape) => ({
      from: shape.from.kind === 'SHAPE' ? shape.from.shapeId : '',
      to: shape.to.kind === 'SHAPE' ? shape.to.shapeId : '',
    }))
    .filter((connector) => connector.from && connector.to),
})

export const DesignPayloadEditor = ({ value, onChange }: DesignPayloadEditorProps) => {
  const emittedInitialValueRef = useRef(false)
  const skipShapeEmitCountRef = useRef(0)
  const draggedNodeIndexRef = useRef<number | null>(null)
  const editor = useDesignEditor('BOUND')
  const payload = useMemo(() => normalizeDesignPayload(value), [value])
  const payloadSignature = useMemo(() => JSON.stringify(payload), [payload])
  const canvas = useMemo(() => normalizeDesignCanvas(payload.canvas), [payload.canvas])
  const shapes = useMemo(() => designPayloadToShapes(payload), [payload])

  useEffect(() => {
    skipShapeEmitCountRef.current = 2
    editor.reset(shapes)
  }, [editor.reset, payloadSignature, shapes])

  useEffect(() => {
    if (emittedInitialValueRef.current) return
    emittedInitialValueRef.current = true
    onChange(payload)
  }, [onChange, payload])

  useEffect(() => {
    if (skipShapeEmitCountRef.current > 0) {
      skipShapeEmitCountRef.current -= 1
      return
    }
    const next = normalizeDesignPayload(shapesToPayload(payload, editor.state.shapes))
    if (JSON.stringify(next) === payloadSignature) return
    onChange(next)
  }, [editor.state.shapes, onChange, payload, payloadSignature])

  const emit = (next: DesignPayload) => onChange(normalizeDesignPayload(next))

  const updateCanvas = (patch: Partial<DesignCanvasSettings>) => {
    emit({ ...payload, canvas: { ...asRecord(payload.canvas), ...patch } })
  }

  const updateNode = (nodeIndex: number, patch: Partial<DesignPayloadNode>) => {
    emit({
      ...payload,
      nodes: payload.nodes.map((node, index) => index === nodeIndex ? { ...node, ...patch } : node),
    })
  }

  const addNode = () => emit({
    ...payload,
    nodes: [...payload.nodes, { id: `node-${payload.nodes.length + 1}`, type: 'stage', x: 120, y: 120, label: 'New node' }],
  })

  const deleteNode = (nodeIndex: number) => {
    const node = payload.nodes[nodeIndex]
    if (!node) return
    emit({
      ...payload,
      nodes: payload.nodes.filter((_, index) => index !== nodeIndex),
      connectors: payload.connectors.filter((connector) => connector.from !== node.id && connector.to !== node.id),
    })
  }

  const reorderNode = (toIndex: number) => {
    const fromIndex = draggedNodeIndexRef.current
    draggedNodeIndexRef.current = null
    if (fromIndex === null) return
    emit({ ...payload, nodes: reorder(payload.nodes, fromIndex, toIndex) })
  }

  const updateConnector = (connectorIndex: number, patch: Partial<DesignPayloadConnector>) => {
    emit({
      ...payload,
      connectors: payload.connectors.map((connector, index) => index === connectorIndex ? { ...connector, ...patch } : connector),
    })
  }

  const addConnector = () => emit({
    ...payload,
    connectors: [...payload.connectors, { from: payload.nodes[0]?.id ?? '', to: payload.nodes[1]?.id ?? payload.nodes[0]?.id ?? '' }],
  })

  const deleteConnector = (connectorIndex: number) => emit({
    ...payload,
    connectors: payload.connectors.filter((_, index) => index !== connectorIndex),
  })

  return (
    <section className="designPayloadEditor" aria-label="Design payload editor">
      <div className="designPayloadEditor__canvasSettings">
        <label>
          <span>Canvas preset</span>
          <select
            aria-label="Canvas preset"
            value={canvas.presetId ?? DESIGN_CANVAS_PRESETS[0].id}
            onChange={(event) => {
              const preset = DESIGN_CANVAS_PRESETS.find((candidate) => candidate.id === event.target.value) ?? DESIGN_CANVAS_PRESETS[0]
              updateCanvas({ presetId: preset.id, grid: preset.grid })
            }}
          >
            {DESIGN_CANVAS_PRESETS.map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
          </select>
        </label>
        <NumberField label="Canvas width" value={canvas.width} onChange={(width) => updateCanvas({ width })} />
        <NumberField label="Canvas height" value={canvas.height} onChange={(height) => updateCanvas({ height })} />
        <NumberField label="Grid" value={canvas.grid} onChange={(grid) => updateCanvas({ grid })} />
      </div>
      <label className="designPayloadEditor__lanes">
        <span>Lanes</span>
        <input
          aria-label="Lanes"
          value={(payload.lanes ?? []).join(', ')}
          onChange={(event) => emit({ ...payload, lanes: parseLanes(event.target.value) })}
        />
      </label>
      <div className="designPayloadEditor__preview">
        <DesignCanvas editor={editor} bindings={new Map()} canvas={canvas} readOnly />
      </div>
      <div className="designPayloadEditor__lists">
        <section className="designPayloadEditor__list" aria-label="Design nodes">
          <header>
            <strong>Nodes</strong>
            <Button type="button" variant="secondary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} onClick={addNode}>Add node</Button>
          </header>
          {payload.nodes.map((node, nodeIndex) => (
            <article
              className="designPayloadEditor__node"
              key={`${node.id}-${nodeIndex}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => reorderNode(nodeIndex)}
            >
              <button type="button" className="designPayloadEditor__drag" draggable aria-label={`Drag node ${nodeIndex + 1}`} onDragStart={() => { draggedNodeIndexRef.current = nodeIndex }}>
                <DragIndicatorRoundedIcon fontSize="inherit" />
              </button>
              <label><span>ID</span><input aria-label={`Node ${nodeIndex + 1} id`} value={node.id} onChange={(event) => updateNode(nodeIndex, { id: event.target.value })} /></label>
              <label><span>Type</span><select aria-label={`Node ${nodeIndex + 1} type`} value={node.type} onChange={(event) => updateNode(nodeIndex, { type: event.target.value })}><option value="stage">stage</option><option value="decision">decision</option></select></label>
              <NumberField label={`Node ${nodeIndex + 1} x`} value={node.x} onChange={(x) => updateNode(nodeIndex, { x })} />
              <NumberField label={`Node ${nodeIndex + 1} y`} value={node.y} onChange={(y) => updateNode(nodeIndex, { y })} />
              <label><span>Label</span><input aria-label={`Node ${nodeIndex + 1} label`} value={node.label} onChange={(event) => updateNode(nodeIndex, { label: event.target.value })} /></label>
              <button type="button" className="designPayloadEditor__iconButton" aria-label={`Delete node ${nodeIndex + 1}`} onClick={() => deleteNode(nodeIndex)}><DeleteRoundedIcon fontSize="inherit" /></button>
            </article>
          ))}
        </section>
        <section className="designPayloadEditor__list" aria-label="Design connectors">
          <header>
            <strong>Connectors</strong>
            <Button type="button" variant="secondary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} onClick={addConnector}>Add connector</Button>
          </header>
          {payload.connectors.map((connector, connectorIndex) => (
            <article className="designPayloadEditor__connector" key={`${connector.from}-${connector.to}-${connectorIndex}`}>
              <label><span>From</span><input aria-label={`Connector ${connectorIndex + 1} from`} value={connector.from} onChange={(event) => updateConnector(connectorIndex, { from: event.target.value })} /></label>
              <label><span>To</span><input aria-label={`Connector ${connectorIndex + 1} to`} value={connector.to} onChange={(event) => updateConnector(connectorIndex, { to: event.target.value })} /></label>
              <button type="button" className="designPayloadEditor__iconButton" aria-label={`Delete connector ${connectorIndex + 1}`} onClick={() => deleteConnector(connectorIndex)}><DeleteRoundedIcon fontSize="inherit" /></button>
            </article>
          ))}
        </section>
      </div>
    </section>
  )
}

const NumberField = ({ label, value, onChange }: { label: string; value: number; onChange: (next: number) => void }) => (
  <label>
    <span>{label.replace(/^Node \d+ /, '').replace(/^Canvas /, '')}</span>
    <NumberStepper
      ariaLabel={label}
      value={value}
      min={0}
      max={label.includes('Grid') ? 128 : 10000}
      step={label.includes('Grid') ? 4 : 10}
      variant="orbit"
      size="sm"
      onChange={onChange}
    />
  </label>
)
